"use client"

import { useTranslation } from "@/lib/i18n/provider"
import { LOCALE_LABELS, SUPPORTED_LOCALES, type Locale } from "@/lib/i18n/dictionaries"
import { useState, useRef, useEffect } from "react"
import { Globe, Check, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

export function LanguageSwitcherCompact() {
  const { locale, setLocale } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground shadow-sm transition-all hover:bg-accent"
        aria-label="Switch language"
      >
        <span>{LOCALE_LABELS[locale].flag}</span>
        <span className="font-medium uppercase">{locale}</span>
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 min-w-[160px] overflow-hidden rounded-xl border border-border bg-popover shadow-xl">
          {SUPPORTED_LOCALES.map((l) => (
            <button
              key={l}
              onClick={() => {
                setLocale(l as Locale)
                setOpen(false)
              }}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors",
                l === locale
                  ? "bg-primary/10 text-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <span className="text-lg">{LOCALE_LABELS[l].flag}</span>
              <span className="flex-1 font-medium">{LOCALE_LABELS[l].native}</span>
              {l === locale && <Check className="h-4 w-4 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function LanguageSwitcherFull() {
  const { locale, setLocale } = useTranslation()
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {SUPPORTED_LOCALES.map((l) => {
        const isActive = l === locale
        return (
          <button
            key={l}
            onClick={() => setLocale(l as Locale)}
            className={cn(
              "group relative flex items-center gap-4 rounded-xl border p-4 text-left transition-all",
              isActive
                ? "border-primary/40 bg-primary/10"
                : "border-border bg-card hover:border-border hover:bg-accent"
            )}
          >
            <div className="text-3xl">{LOCALE_LABELS[l].flag}</div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-foreground">
                {LOCALE_LABELS[l].native}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {l}
              </div>
            </div>
            {isActive && (
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                <Check className="h-3.5 w-3.5 text-primary-foreground" />
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

export { Globe }
